/**
 * The session the panels under a room are about, whichever room it is.
 *
 * The Bug Hunt knows a session from its game state; the engineering floor knows it from the
 * activity feed. Both say the same facts in slightly different shapes, so each is turned into this
 * one before it reaches the panels — the panels never care which room they are in.
 */

import type { ActivitySession, ActivityState, BugHuntSession, WorkCategory, WorkSessionDetail } from '../lib/types'

export interface PanelSession {
  workSessionId: string
  /** The ticket or specification it is for, as people call it. */
  reference: string | null
  /** The task's title, or what an exploration is about. */
  title: string | null
  category: WorkCategory | null
  activityState: ActivityState
  progressPercent: number
  /** Exploration has no plan to measure against, so no percentage to show. */
  hasProgress: boolean
  currentStepTitle: string | null
  currentStepNote: string | null
  /** The model, or the tool when the model is unknown. */
  aiName: string | null
  startedAt: string
  durationSeconds: number
  /** Null when the room does not know; the panels then read it from the session detail. */
  tests: { passed: number; total: number } | null
}

export function fromBugHunt(session: BugHuntSession): PanelSession {
  return {
    workSessionId: session.workSessionId,
    reference: session.taskExternalId,
    title: session.taskTitle ?? session.currentStepTitle,
    category: 'Bug',
    activityState: session.activityState,
    progressPercent: session.progressPercent,
    hasProgress: true,
    currentStepTitle: session.currentStepTitle,
    currentStepNote: session.currentStepNote,
    aiName: session.modelName ?? session.providerName,
    startedAt: session.startedAt,
    durationSeconds: session.durationSeconds,
    tests: session.testsTotal > 0 ? { passed: session.testsPassed, total: session.testsTotal } : null,
  }
}

export function fromActivity(session: ActivitySession): PanelSession {
  const reference =
    session.taskExternalId ??
    (session.specificationKey ? `${session.specificationKind === 'Prd' ? 'PRD' : 'SDD'} ${session.specificationKey}` : null)

  return {
    workSessionId: session.workSessionId,
    reference,
    title: session.subject ?? session.specificationTitle ?? session.currentStepTitle,
    category: session.category,
    activityState: session.activityState,
    progressPercent: session.progressPercent,
    hasProgress: session.kind === 'TaskWork',
    currentStepTitle: session.currentStepTitle,
    currentStepNote: session.currentStepNote,
    aiName: session.modelName ?? session.providerName,
    startedAt: session.startedAt,
    durationSeconds: session.durationSeconds,
    tests: null,
  }
}

/** The latest test run's counts, when the room did not carry them. */
export function testsFrom(session: PanelSession, detail: WorkSessionDetail | undefined): { passed: number; total: number } | null {
  if (session.tests) return session.tests
  const latest = [...(detail?.testRuns ?? [])]
    .filter((run) => run.total > 0)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]
  return latest ? { passed: latest.passed, total: latest.total } : null
}
